import { useCallback } from 'react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { useSchemaQueryUpdaters } from '@frontend/react-query-helpers';
import { serviceName } from '../service';
import { ServiceQueries } from '../types';

export type ProviderMutationContext = {
  originalData?: Provider;
  updatedData?: Provider;
};

export type AppointmentTypeMutationContext = {
  originalData?: AppointmentType;
  updatedData?: AppointmentType;
};

/**
 * This hook is intended to be used by react-query mutation hooks internally in api libraries.
 * Other use cases should use the corresponding mutations instead.
 */
export const useSchedulerV3QueryUpdaters = () => {
  const queryUpdaters = useSchemaQueryUpdaters<ServiceQueries>(serviceName);

  const updateProvider = useCallback(
    (providerId: string, newValues: Partial<Provider & { childLocationId: string }>): ProviderMutationContext => {
      let originalData: Provider | undefined;
      let updatedData: Provider | undefined;

      const getUpdatedProvider = (oldValues: Provider): Provider => {
        const newData = { ...oldValues, ...newValues };
        if (!originalData) originalData = oldValues;
        if (!updatedData) updatedData = newData;
        return newData;
      };

      queryUpdaters.updateQuery({
        endpointName: 'GetProvider',
        queryFilters: {
          predicate: ({ queryKey }) => queryKey[2].id === providerId,
          exact: false,
        },
        updater: (oldData) => ({ provider: getUpdatedProvider(oldData.provider) }),
      });

      queryUpdaters.updateQuery({
        endpointName: 'ListProviders',
        queryFilters: {
          predicate: ({ state }) => (state.data?.providers ?? []).some((provider) => provider.id === providerId),
          exact: false,
        },
        updater: (oldData) => ({
          ...oldData,
          providers: oldData.providers.map((p) => (p.id === providerId ? getUpdatedProvider(p) : p)),
        }),
      });

      queryUpdaters.updateQuery({
        endpointName: 'ListProvidersMulti',
        queryFilters: {
          predicate: ({ state }) =>
            (state.data?.providers ?? []).some(
              (provider) => provider.id === providerId && provider.locationId === newValues.childLocationId
            ),
          exact: false,
        },
        updater: (oldData) => ({
          ...oldData,
          providers: oldData.providers.map((p) =>
            p.id === providerId && p.locationId === newValues.childLocationId ? getUpdatedProvider(p) : p
          ),
        }),
      });

      return { originalData, updatedData };
    },
    [queryUpdaters]
  );

  const updateAppointmentType = useCallback(
    (
      apptTypeId: string,
      newValues: Partial<AppointmentType & { childLocationId: string }>
    ): AppointmentTypeMutationContext => {
      let originalData: AppointmentType | undefined;
      let updatedData: AppointmentType | undefined;

      const getUpdatedAppointmentType = (oldValues: AppointmentType): AppointmentType => {
        const newData = { ...oldValues, ...newValues };
        if (!originalData) originalData = oldValues;
        if (!updatedData) updatedData = newData;
        return newData;
      };

      queryUpdaters.updateQuery({
        endpointName: 'GetAppointmentType',
        queryFilters: {
          predicate: ({ queryKey }) => queryKey[2].appointmentTypeId === apptTypeId,
          exact: false,
        },
        updater: (oldData) => ({ appointmentType: getUpdatedAppointmentType(oldData.appointmentType) }),
      });

      queryUpdaters.updateQuery({
        endpointName: 'ListAppointmentTypes',
        queryFilters: {
          predicate: ({ state }) => (state.data?.appointmentTypes ?? []).some((t) => t.id === apptTypeId),
          exact: false,
        },
        updater: (oldData) => ({
          ...oldData,
          appointmentTypes: oldData.appointmentTypes.map((t) =>
            t.id === apptTypeId ? getUpdatedAppointmentType(t) : t
          ),
        }),
      });

      queryUpdaters.updateQuery({
        endpointName: 'ListAppointmentTypesMulti',
        queryFilters: {
          predicate: ({ state }) =>
            (state.data?.appointmentTypes ?? []).some(
              (t) => t.id === apptTypeId && t.locationId === newValues.childLocationId
            ),
          exact: false,
        },
        updater: (oldData) => ({
          ...oldData,
          appointmentTypes: oldData.appointmentTypes.map((t) =>
            t.id === apptTypeId && t.locationId === newValues.childLocationId ? getUpdatedAppointmentType(t) : t
          ),
        }),
      });
      return { originalData, updatedData };
    },
    [queryUpdaters]
  );

  const deleteCalendarEvent = useCallback(
    (calendarEventId: string) => {
      queryUpdaters.updateQuery({
        endpointName: 'ListCalendarEvents',
        queryFilters: {
          predicate: ({ state }) =>
            (state.data?.events ?? []).some((event) => event.id && event.id === calendarEventId),
          exact: false,
        },
        updater: (oldData) => ({
          ...oldData,
          events: oldData.events.filter((event) => event.id && event.id !== calendarEventId),
        }),
      });
      queryUpdaters.updateQuery({
        endpointName: 'ListCalendarEventsMulti',
        queryFilters: {
          predicate: ({ state }) =>
            (state.data?.events ?? []).some((event) => event.id && event.id === calendarEventId),
          exact: false,
        },
        updater: (oldData) => ({
          ...oldData,
          events: oldData.events.filter((event) => event.id && event.id !== calendarEventId),
        }),
      });
      queryUpdaters.invalidateQueries({
        endpointName: 'GetCalendarEvent',
        queryFilters: {
          predicate: ({ queryKey }) => !!queryKey[2].eventId && calendarEventId === queryKey[2].eventId,
          exact: false,
        },
      });
      queryUpdaters.invalidateQueries({
        endpointName: 'ListCalendarEventsByAttendee',
        queryFilters: {
          exact: false,
        },
      });
    },
    [queryUpdaters]
  );

  return {
    updateProvider,
    updateAppointmentType,
    deleteCalendarEvent,
    ...queryUpdaters,
  };
};
